import { useDispatch, useSelector } from 'react-redux';
import styles from './styles.module.css';
import { RootState } from '../../../store';
import ShareModalButton from './ShareModalButton';
import DownloadButton from './DownloadButton';
import DiscoverRomansLink from './DiscoverRomansLink';
import Switch from './Switch';
import { videoPanelStyle } from '../../../store/screen/panelSelectors';
import useInternationalization from '../../../hooks/useInternationalization';
import {
  setAlwaysFitPanelWidth,
  setAutoadvance,
  setAutoplay,
  setShowBBBResourceButton,
  setShowClosedCaptions,
} from '../../../store/settings';

import autoVideo from '../../../assets/videos/autoadvance.mp4';
import { VideoTooltip } from '../../VideoTooltip';

const ActionBar = (): JSX.Element => {
  const dispatch = useDispatch();
  const {
    autoplay,
    autoadvance,
    darkMode,
    alwaysFitPanelWidth,
    showBBBResourceButton,
    showClosedCaptions,
  } = useSelector((state: RootState) => state.settings);

  const { code, bookOrSlug, textVersion } = useSelector((state: RootState) => state.params);
  const showTextPanel = textVersion !== undefined;
  const isAslvRomans = code?.toUpperCase() === 'ASLV' && bookOrSlug?.toUpperCase() === 'ROM';

  const autoplayHandler = (value: boolean) => {
    dispatch(setAutoplay(value));
  };

  const showClosedCaptionsHandler = (value: boolean) => {
    dispatch(setShowClosedCaptions(value));
  };

  const autoadvanceHandler = (value: boolean) => {
    dispatch(setAutoadvance(value));
  };

  const bbbResourceButtonHandler = (value: boolean) => {
    dispatch(setShowBBBResourceButton(value));
  };

  const panelWidthHandler = (value: boolean) => {
    dispatch(setAlwaysFitPanelWidth(value));
  };

  const panelStyle = useSelector(videoPanelStyle);
  const isMobileDevice = panelStyle.width < 600;
  const stackDiscoverRomans = isAslvRomans && panelStyle.width < 740;
  const { getIntlText } = useInternationalization();

  const container = `${styles.container} ${darkMode && styles.containerDarkMode} ${
    isMobileDevice && styles.containerMobile
  } ${stackDiscoverRomans && styles.containerStackDiscover}`;

  const switchesContainer = `${styles.switchesContainer} ${
    isMobileDevice && styles.switchesContainerMobile
  } ${isMobileDevice && isAslvRomans && styles.switchesContainerMobileWithDiscover}`;

  const buttonsContainer = `${styles.buttonsContainer} ${
    isMobileDevice && styles.buttonsContainerMobile
  }`;

  const discoverRomansContainer = `${styles.discoverRomansContainer} ${
    stackDiscoverRomans && styles.discoverRomansContainerStacked
  }`;

  return (
    <div className={container}>
      <div className={switchesContainer}>
        {!isMobileDevice && showTextPanel && (
          <Switch
            label="Stretch to fit"
            value={alwaysFitPanelWidth}
            setValue={panelWidthHandler}
            darkMode={darkMode}
            isMobile={isMobileDevice}
          />
        )}
        <Switch
          label={getIntlText('autoplay')}
          value={autoplay}
          setValue={autoplayHandler}
          darkMode={darkMode}
          isMobile={isMobileDevice}
        />
        <Switch
          label={getIntlText('auto_advance')}
          value={autoadvance}
          setValue={autoadvanceHandler}
          darkMode={darkMode}
          isMobile={isMobileDevice}
        />
        <div style={{ marginLeft: 5 }}>
          <VideoTooltip
            id="autoadvanceExplanation"
            src={autoVideo}
            description={getIntlText(
              'automatically_jump_to_the_next_video_when_the_current_video_ends'
            )}
          />
        </div>
        {!isAslvRomans && (
          <Switch
            label={getIntlText('bbb_resources')}
            value={showBBBResourceButton}
            setValue={bbbResourceButtonHandler}
            darkMode={darkMode}
            isMobile={isMobileDevice}
          />
        )}
        <Switch
          label="Show CC"
          value={showClosedCaptions}
          setValue={showClosedCaptionsHandler}
          darkMode={darkMode}
          isMobile={isMobileDevice}
        />
      </div>
      {isAslvRomans && (
        <div className={discoverRomansContainer}>
          <DiscoverRomansLink />
        </div>
      )}
      <div className={buttonsContainer}>
        <ShareModalButton darkMode={darkMode} isMobile={isMobileDevice} />
        <DownloadButton darkMode={darkMode} isMobile={isMobileDevice} />
      </div>
    </div>
  );
};

export default ActionBar;
